import { Segment, AidStation, CalibrationProfile } from '../types'
import { calculateSegmentPace } from './calculations'

/**
 * Represents a chronological event in the race timeline
 */
export interface TimelineEvent {
  type: 'segment_end' | 'aid_station'
  distance: number
  cumulativeTime: number // Time to reach this point (arrival)
  cumulativeTimeAfterStop: number // Time after any stop at this point (departure)
  data: Segment | AidStation
}

/**
 * Central timeline calculator that handles all ETA logic consistently
 *
 * This is the single source of truth for race timing calculations.
 * It processes segments and aid stations in chronological order,
 * properly accounting for all stop times.
 */
export class RaceTimeline {
  private events: TimelineEvent[] = []

  constructor(
    segments: Segment[],
    aidStations: AidStation[] = [],
    calibrationProfile?: CalibrationProfile | {
      beta0_base_pace_min_per_km: number;
      beta1_uphill_min_per_m_per_km: number;
      beta2_downhill_min_per_m_per_km: number;
      beta3_fatigue_min_per_km: number;
      use_fatigue: boolean;
    }
  ) {
    this.buildTimeline(segments, aidStations, calibrationProfile)
  }

  private buildTimeline(
    segments: Segment[],
    aidStations: AidStation[],
    calibrationProfile?: CalibrationProfile | {
      beta0_base_pace_min_per_km: number;
      beta1_uphill_min_per_m_per_km: number;
      beta2_downhill_min_per_m_per_km: number;
      beta3_fatigue_min_per_km: number;
      use_fatigue: boolean;
    }
  ) {
    // Step 1: Calculate segment ETAs INCLUDING aid station stops that occur within them
    const segmentETAs = this.calculateSegmentETAsWithAidStations(segments, aidStations, calibrationProfile)

    // Step 2: Calculate aid station ETAs based on segment timeline
    const aidStationETAs = this.calculateAidStationETAs(segments, aidStations, calibrationProfile)

    // Step 3: Build events list
    this.events = [
      ...segmentETAs,
      ...aidStationETAs
    ].sort((a, b) => a.distance - b.distance)
  }

  /**
   * Calculate segment ETAs accounting for aid station stops within each segment
   */
  private calculateSegmentETAsWithAidStations(
    segments: Segment[],
    aidStations: AidStation[],
    calibrationProfile?: any
  ): TimelineEvent[] {
    const sortedSegments = segments.sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
    const sortedAidStations = aidStations.sort((a, b) => a.at_distance_km - b.at_distance_km)

    const events: TimelineEvent[] = []
    let cumulativeDistance = 0
    let cumulativeTime = 0

    for (const segment of sortedSegments) {
      const segmentStart = cumulativeDistance
      const segmentEnd = cumulativeDistance + segment.distance_km

      // Find aid stations within this segment
      const aidStationsInSegment = sortedAidStations.filter(
        station => station.at_distance_km > segmentStart && station.at_distance_km <= segmentEnd
      )

      // Calculate pace for this segment
      const pace = calculateSegmentPace(segment, calibrationProfile, segmentStart)
      const segmentTravelTime = pace * segment.distance_km
      const segmentStopTime = segment.planned_stop_min || 0

      // Add segment travel time
      cumulativeTime += segmentTravelTime + segmentStopTime

      // Add aid station stop times that occur within this segment
      const totalAidStationStopTime = aidStationsInSegment.reduce(
        (total, station) => total + (station.planned_stop_min || 0),
        0
      )
      cumulativeTime += totalAidStationStopTime

      // Update distance
      cumulativeDistance = segmentEnd

      events.push({
        type: 'segment_end',
        distance: cumulativeDistance,
        cumulativeTime: cumulativeTime - segmentStopTime - totalAidStationStopTime, // Arrival (segment travel only)
        cumulativeTimeAfterStop: cumulativeTime, // Departure (including all stops)
        data: segment
      })
    }

    return events
  }

  /**
   * Calculate aid station ETAs based on simple distance calculation
   */
  private calculateAidStationETAs(
    segments: Segment[],
    aidStations: AidStation[],
    calibrationProfile?: any
  ): TimelineEvent[] {
    return aidStations.map(station => {
      const arrivalTime = this.calculateSimpleTimeToDistance(station.at_distance_km, segments, calibrationProfile)
      const stationStopTime = station.planned_stop_min || 0
      const departureTime = arrivalTime + stationStopTime

      return {
        type: 'aid_station' as const,
        distance: station.at_distance_km,
        cumulativeTime: arrivalTime,
        cumulativeTimeAfterStop: departureTime,
        data: station
      }
    })
  }

  /**
   * Simple calculation of time to reach a distance based only on segments
   * (Used for aid station ETAs - no circular dependency)
   */
  private calculateSimpleTimeToDistance(
    targetDistance: number,
    segments: Segment[],
    calibrationProfile?: CalibrationProfile | {
      beta0_base_pace_min_per_km: number;
      beta1_uphill_min_per_m_per_km: number;
      beta2_downhill_min_per_m_per_km: number;
      beta3_fatigue_min_per_km: number;
      use_fatigue: boolean;
    }
  ): number {
    let cumulativeDistance = 0
    let cumulativeTime = 0

    for (const segment of segments) {
      const segmentEndDistance = cumulativeDistance + segment.distance_km

      if (segmentEndDistance <= targetDistance) {
        // Full segment before target
        const pace = calculateSegmentPace(segment, calibrationProfile, cumulativeDistance)
        const segmentTime = pace * segment.distance_km
        cumulativeTime += segmentTime + (segment.planned_stop_min || 0)
        cumulativeDistance = segmentEndDistance
      } else if (cumulativeDistance < targetDistance) {
        // Target is within this segment
        const remainingDistance = targetDistance - cumulativeDistance
        const pace = calculateSegmentPace(segment, calibrationProfile, cumulativeDistance)
        const partialTime = pace * remainingDistance
        cumulativeTime += partialTime
        break
      } else {
        // Target is before this segment starts
        break
      }
    }

    return cumulativeTime
  }

  /**
   * Calculate time to reach a specific distance, accounting for all previous stops
   */
  private calculateTimeToDistance(
    targetDistance: number,
    segments: Segment[],
    calibrationProfile?: CalibrationProfile | {
      beta0_base_pace_min_per_km: number;
      beta1_uphill_min_per_m_per_km: number;
      beta2_downhill_min_per_m_per_km: number;
      beta3_fatigue_min_per_km: number;
      use_fatigue: boolean;
    }
  ): number {
    let cumulativeDistance = 0
    let cumulativeTime = 0

    for (const segment of segments) {
      const segmentEndDistance = cumulativeDistance + segment.distance_km

      if (segmentEndDistance <= targetDistance) {
        // Full segment before target
        const pace = calculateSegmentPace(segment, calibrationProfile, cumulativeDistance)
        const segmentTime = pace * segment.distance_km
        cumulativeTime += segmentTime + (segment.planned_stop_min || 0)
        cumulativeDistance = segmentEndDistance
      } else if (cumulativeDistance < targetDistance) {
        // Target is within this segment
        const remainingDistance = targetDistance - cumulativeDistance
        const pace = calculateSegmentPace(segment, calibrationProfile, cumulativeDistance)
        const partialTime = pace * remainingDistance
        cumulativeTime += partialTime
        break
      } else {
        // Target is before this segment starts
        break
      }
    }

    // Add time from any aid stations that come before this distance
    for (const event of this.events) {
      if (event.type === 'aid_station' && event.distance < targetDistance) {
        const station = event.data as AidStation
        const stationStopTime = station.planned_stop_min || 0
        // Only add stop time if this aid station actually extends our timeline
        if (event.cumulativeTime + stationStopTime > cumulativeTime) {
          cumulativeTime = event.cumulativeTime + stationStopTime
        }
      }
    }

    return cumulativeTime
  }

  /**
   * Get ETA for any distance along the course
   */
  public getETAAtDistance(distance: number): number {
    // If we have an exact event at this distance, use it
    const exactEvent = this.events.find(e => Math.abs(e.distance - distance) < 0.001)
    if (exactEvent) {
      return exactEvent.cumulativeTime
    }

    // Calculate ETA for this distance
    return this.calculateTimeToDistance(distance, this.getSegments(), undefined)
  }

  /**
   * Get ETA after stops for any distance along the course
   */
  public getETAAfterStopsAtDistance(distance: number): number {
    const exactEvent = this.events.find(e => Math.abs(e.distance - distance) < 0.001)
    if (exactEvent) {
      return exactEvent.cumulativeTimeAfterStop
    }

    return this.getETAAtDistance(distance)
  }

  /**
   * Get all timeline events in chronological order
   */
  public getEvents(): TimelineEvent[] {
    return [...this.events].sort((a, b) => a.distance - b.distance)
  }

  /**
   * Get segment data with calculated timing from timeline events
   */
  public getSegmentData(): Array<{
    segment: Segment
    pace: number
    travelTime: number
    stopTime: number
    totalTime: number
    cumulativeDistance: number
    arrivalTime: number
    departureTime: number
  }> {
    const segmentEvents = this.events.filter(e => e.type === 'segment_end')

    return segmentEvents.map(event => {
      const segment = event.data as Segment
      const pace = calculateSegmentPace(segment)
      const travelTime = pace * segment.distance_km
      const stopTime = segment.planned_stop_min || 0
      const totalTime = travelTime + stopTime

      return {
        segment,
        pace,
        travelTime,
        stopTime,
        totalTime,
        cumulativeDistance: event.distance,
        arrivalTime: event.cumulativeTime,
        departureTime: event.cumulativeTimeAfterStop
      }
    })
  }

  /**
   * Get aid station data with calculated timing
   */
  public getAidStationData(): Array<{
    station: AidStation
    arrivalTime: number
    departureTime: number
    stopTime: number
  }> {
    return this.events
      .filter(e => e.type === 'aid_station')
      .map(event => {
        const station = event.data as AidStation
        const stopTime = station.planned_stop_min || 0

        return {
          station,
          arrivalTime: event.cumulativeTime,
          departureTime: event.cumulativeTimeAfterStop,
          stopTime
        }
      })
  }

  private getSegments(): Segment[] {
    return this.events
      .filter(e => e.type === 'segment_end')
      .map(e => e.data as Segment)
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
  }
}

/**
 * Convenience function to create timeline and get ETA at distance
 */
export function calculateETAAtDistance(
  distance: number,
  segments: Segment[],
  aidStations: AidStation[] = [],
  calibrationProfile?: CalibrationProfile | {
    beta0_base_pace_min_per_km: number;
    beta1_uphill_min_per_m_per_km: number;
    beta2_downhill_min_per_m_per_km: number;
    beta3_fatigue_min_per_km: number;
    use_fatigue: boolean;
  }
): number {
  const timeline = new RaceTimeline(segments, aidStations, calibrationProfile)
  return timeline.getETAAtDistance(distance)
}

/**
 * Convenience function to create timeline and get all segment data
 */
export function calculateSegmentTimeline(
  segments: Segment[],
  aidStations: AidStation[] = [],
  calibrationProfile?: CalibrationProfile | {
    beta0_base_pace_min_per_km: number;
    beta1_uphill_min_per_m_per_km: number;
    beta2_downhill_min_per_m_per_km: number;
    beta3_fatigue_min_per_km: number;
    use_fatigue: boolean;
  }
) {
  const timeline = new RaceTimeline(segments, aidStations, calibrationProfile)
  return {
    segments: timeline.getSegmentData(),
    aidStations: timeline.getAidStationData(),
    events: timeline.getEvents()
  }
}